import { FadeUp, Overline, MaskedLines } from "../components/Reveal";
import Seo from "../components/Seo";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { IMAGES, JOURNEY } from "../data/content";

const ChapterImage = ({ src, alt, testid }) => (
  <div className="relative overflow-hidden border border-white/10" data-testid={testid}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-[340px] md:h-[420px] object-cover object-top hover:scale-[1.03] transition-transform duration-[1.2s] ease-out"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 via-transparent to-transparent" />
  </div>
);

export default function Journey() {
  return (
    <main data-testid="journey-page" className="pt-40">
      <Seo
        title="The Journey — Gurpreet Paul"
        description="From India to the C-Suite: Gurpreet Paul's five-chapter journey through Reckitt Benckiser, PepsiCo, thirteen years at Coca-Cola, entrepreneurship, and founding ThirsTea USA."
      />
      <header className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <FadeUp>
          <Overline>The Journey</Overline>
        </FadeUp>
        <MaskedLines
          lines={["Five chapters.", "One relentless", "pursuit."]}
          className="font-display font-medium tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-paper mt-6"
          delay={0.15}
        />
        <FadeUp delay={0.4}>
          <p className="mt-8 max-w-2xl text-white/55 text-base md:text-lg leading-relaxed font-body font-light">
            Born in India, immigrating to the United States in 2002 — from sales floors at Reckitt Benckiser
            and PepsiCo, to thirteen years inside Coca-Cola, to founding one of America's most exciting franchise brands.
          </p>
        </FadeUp>
      </header>

      <EditorialMarquee />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="journey-timeline">
        {JOURNEY.map((ch, i) => (
          <FadeUp key={ch.chapter} delay={0.05}>
            <article
              data-testid={`journey-chapter-${ch.chapter}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-16 border-t border-white/10 first:border-t-0"
            >
              <div className="lg:col-span-3">
                <span className="font-display font-light text-7xl lg:text-8xl text-stroke leading-none block">
                  {ch.chapter}
                </span>
                <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-brand font-body">{ch.era}</p>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-display font-medium tracking-tight text-3xl md:text-4xl text-paper">
                  {ch.title}
                </h2>
                <p className="mt-2 text-[13px] uppercase tracking-[0.15em] text-white/40 font-body">{ch.role}</p>
                <p className="mt-6 text-white/55 text-base leading-relaxed font-body font-light">{ch.description}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {ch.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/15 px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] text-white/50 font-body"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-3 flex items-start">
                {i === 2 && <ChapterImage src={IMAGES.portraitBrown} alt="Gurpreet Paul — corporate years" testid="journey-image-corporate" />}
                {i === 4 && <ChapterImage src={IMAGES.desk} alt="Gurpreet Paul at ThirsTea USA headquarters" testid="journey-image-thirstea" />}
                {i === 0 && (
                  <div className="border border-white/10 p-8 w-full bg-[#111113]" data-testid="journey-education-card">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-brand font-body mb-5">Education</p>
                    <ul className="space-y-4 text-sm text-white/60 font-body font-light leading-relaxed">
                      <li><span className="text-paper">MBA</span> — Institute of Management Studies, 1997</li>
                      <li><span className="text-paper">Int'l Sales & Export Mgmt</span> — IIEM Bangalore, 1997</li>
                      <li><span className="text-paper">BA Economics & Business</span> — 1994</li>
                    </ul>
                  </div>
                )}
              </div>
            </article>
          </FadeUp>
        ))}
      </section>

      <section data-testid="journey-beyond" className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-7">
            <Overline>Beyond Business</Overline>
            <h2 className="font-display font-medium tracking-tight text-3xl md:text-4xl text-paper mt-6">
              Community, family & the long game
            </h2>
            <p className="mt-6 text-white/55 text-base leading-relaxed font-body font-light max-w-2xl">
              President of the Asian Business Resource Group of Southern California, and actively involved in
              charitable initiatives within the Indian community — donating products from ThirsTea locations to
              those in need. Off the clock: tennis and family. On the horizon: 50 ThirsTea locations across the
              United States and overseas, and helping families achieve financial independence through franchising.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <ChapterImage src={IMAGES.portraitGrey} alt="Gurpreet Paul portrait" testid="journey-image-portrait" />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
